<template>
  <div class="row">
    <div class="col-sm-8 col-sm-offset-2">
      <div class="p-t-15 p-b-15">
        <p class="f-500 c-black text-center">PAYE settings</p>

        <p class="c-gray">
          For some jobs in Worksome, you can only be paid through a payroll (PAYE) even if you have
          a Limited company. You have indicated that you are prepared to be taxed as an Employee
          with Worksome handling the taxes and we therefore need the following information from you.
        </p>

        <div class="form-group">
          <label for="title" class="col-md-4 control-label">Title *</label>
          <div class="col-md-4">
            <select id="title" v-model="title" name="title" class="form-control">
              <option :value="null" selected>Choose your title</option>

              <option v-for="titleOption of titleOptions" :key="titleOption" :value="titleOption">
                {{ titleOption }}
              </option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label for="sex" class="col-md-4 control-label">Sex *</label>
          <div class="col-md-4">
            <select id="sex" v-model="sex" name="sex" class="form-control">
              <option :value="null" selected>Choose your sex</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label for="birthday" class="col-md-4 control-label">Date of birth *</label>
          <div class="col-md-3">
            <input
              id="birthday"
              v-model="birthday"
              type="text"
              name="birthday"
              class="form-control"
              placeholder="DD/MM/YYYY"
            />
          </div>
        </div>

        <div class="form-group">
          <label for="title" class="col-md-4 control-label">Nationality *</label>
          <div class="col-md-6">
            <select id="nationality" v-model="nationality" name="nationality" class="form-control">
              <option :value="null" selected>Choose your nationality</option>
              <option v-for="name of nationalities" :key="name" :value="name">
                {{ name }}
              </option>
            </select>
            <span class="small m-l-10 c-gray">
              Be prepared to submit further documentation for an identity and right to work check.
            </span>
          </div>
        </div>

        <div class="form-group" :class="{ 'has-error': !validNino }">
          <label for="title" class="col-md-4 control-label">National insurance number *</label>
          <div class="col-md-6">
            <input
              v-model="nationalInsuranceNumber"
              type="text"
              name="national_insurance_number"
              placeholder="Enter your national insurance number"
              class="form-control"
              @blur="validateNino"
            />
            <span v-if="!validNino" class="help-block error-help-block">
              Please use a valid format for the National Insurance Number.
              <br />
              Use uppercase letters, numbers and spaces only.
              <br />
              E.g. AA 12 34 56 C
            </span>
          </div>
        </div>

        <div class="form-group">
          <label for="title" class="col-md-4 control-label">Starter form *</label>
          <div class="col-md-6">
            <div v-for="(name, value) of starterFormOptions" :key="value" class="radio m-t-10">
              <label>
                <input
                  v-model="starterFormType"
                  name="starter_form_type"
                  type="radio"
                  :value="value"
                  :disabled="!canChangeTaxCode"
                />
                <i class="input-helper"></i>
                <span>
                  {{ name }}
                  <PopperIconHelp
                    v-if="starterFormHelpers[value] !== null"
                    :default-slot-is-inline="true"
                    class="inline text-left"
                  >
                    <p v-for="text of starterFormHelpers[value]" :key="text">
                      {{ text }}
                    </p>
                  </PopperIconHelp>
                </span>
              </label>
            </div>
            <span class="small m-l-10 c-gray">
              Based on your starter form choice, you will be assigned a tax code. Therefore please
              make sure to answer correctly here. Going forward you can always refer to your latest
              Worksome payslip for your most recent tax code as HMRC may adjust this where
              necessary.
            </span>
          </div>
        </div>

        <div class="form-group">
          <label for="title" class="col-md-4 control-label">Student loan deductions *</label>
          <div class="col-md-6">
            <select
              v-model="studentLoanDeduction"
              class="form-control"
              name="student_loan_deduction"
            >
              <option :value="null">Choose an option</option>
              <option
                v-for="studentLoanDeductionOption of studentLoanDeductionOptions"
                :key="studentLoanDeductionOption"
                :value="studentLoanDeductionOption"
              >
                {{ studentLoanDeductionOption }}
              </option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label for="title" class="col-md-4 control-label">Post graduate loan deductions *</label>
          <div class="col-md-6">
            <select
              v-model="postGraduateLoanDeduction"
              class="form-control"
              name="post_graduate_loan_deduction"
            >
              <option :value="null">Choose an option</option>
              <option
                v-for="(postGraduateLoanDeductionOption, name) of postGraduateLoanDeductionOptions"
                :key="postGraduateLoanDeductionOption"
                :value="name"
              >
                {{ postGraduateLoanDeductionOption }}
              </option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label for="title" class="col-md-4 control-label">Payment frequency *</label>
          <div class="col-md-6">
            <select v-model="paymentFrequency" class="form-control" name="payment_frequency">
              <option :value="null">Choose an option</option>
              <option v-for="option of paymentFrequencyOptions" :key="option" :value="option">
                {{ option }}
              </option>
            </select>
            <span class="small m-l-10 c-gray">
              {{
                $t(
                  'Are you more likely to bill weekly or monthly to your clients? It is important to get this right as it directly affects how HMRC attributes your tax allowance.',
                )
              }}
            </span>
          </div>
        </div>

        <div class="form-group m-t-20 text-center">
          <input type="submit" value="Save" class="btn btn-primary btn-lg" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PopperIconHelp from '@/components/PopperIconHelp.vue'

export default {
  components: { PopperIconHelp },
  props: {
    defaultComplianceData: {
      type: Object,
      default() {
        return {}
      },
    },
    canChangeTaxCode: {
      type: Boolean,
      required: true,
    },
    nationalities: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      title: this.defaultComplianceData?.title ?? null,
      sex: this.defaultComplianceData?.sex ?? null,
      birthday: this.defaultComplianceData?.birthday ?? null,
      nationality: this.defaultComplianceData?.nationality ?? null,
      nationalInsuranceNumber: this.defaultComplianceData?.national_insurance_number ?? null,
      starterFormType: this.defaultComplianceData?.starter_form_type ?? null,
      studentLoanDeduction: this.defaultComplianceData?.student_loan_deduction ?? null,
      postGraduateLoanDeduction: this.defaultComplianceData?.post_graduate_loan_deduction ?? null,
      paymentFrequency: this.defaultComplianceData?.payment_frequency ?? null,
      nameOnBankAccount: this.defaultComplianceData?.name_on_bank_account ?? null,
      hasInternationalBankAccount:
        this.defaultComplianceData?.has_international_bank_account ?? null,
      internationalBankAddress: this.defaultComplianceData?.international_bank_address ?? null,

      studentLoanDeductionOptions: ['Not Applicable', 'Plan 1', 'Plan 2', 'Plan 4', 'Plan 5'],
      postGraduateLoanDeductionOptions: {
        0: 'Not Applicable',
        1: 'Applicable',
      },
      paymentFrequencyOptions: ['Weekly', 'Monthly'],
      starterFormOptions: {
        a: 'This is my first employment since April 6th (A)',
        b: 'Since April 6th I have had other jobs but this is now my only employment (B)',
        c: 'I have another employment (C)',
      },
      starterFormHelpers: {
        a: null,
        b: null,
        c: [
          'Option C also applies if you have a Limited company from which you receive a salary.',
          'Remember that your tax allowance can only be claimed on your first employment and' +
            'any subsequent employments will suffer tax on all income earned.',
        ],
      },
      titleOptions: ['Mr', 'Mrs', 'Ms', 'Dr'],
      validNino: true,
    }
  },
  mounted() {
    this.validateNino()
  },
  methods: {
    validateNino() {
      const regex =
        /^(?!BG|GB|NK|KN|TN|NT|ZZ)[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z](?:\s*\d{2}){3}\s*[A-D]$/
      const nino = this.nationalInsuranceNumber
      if (!nino) {
        this.validNino = true
      } else {
        this.validNino = regex.test(nino)
      }
    },
  },
}
</script>

<style scoped></style>
